import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { Destroyable, SaveForm, Toaster, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { finalize } from 'rxjs/operators'
import {
  BDC_RTB_REJECTION_TEXT_MAX,
  bdcRtbDisclaimer,
  bdcRtbRejectionReasons,
  BdcRtbRejectionReason,
} from '../bdc-request-to-book.model'
import { BdcRequestToBookService } from '../bdc-request-to-book.service'

// TV4-6322: decline a Booking.com Request to Book request with one of Booking.com's reasons.
@Component({
  selector: 'app-decline-bdc-request-dialog',
  templateUrl: './decline-bdc-request-dialog.component.html',
  styleUrls: ['./decline-bdc-request-dialog.component.scss'],
  host: { class: 'modal-content' },
})
export class DeclineBdcRequestDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    rejectionReason: [undefined as BdcRtbRejectionReason, [Validators.required]],
    rejectionReasonText: ['', [Validators.maxLength(BDC_RTB_REJECTION_TEXT_MAX)]],
  })

  reasons = bdcRtbRejectionReasons
  disclaimer = bdcRtbDisclaimer
  textMax = BDC_RTB_REJECTION_TEXT_MAX
  isLoading = false

  constructor(
    public dialogRef: MatDialogRef<DeclineBdcRequestDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private rtbService: BdcRequestToBookService,
    @Inject(MAT_DIALOG_DATA) public data: { inquiry: Inquiry }
  ) {
    super()
  }

  get isNotComfortable() {
    return this.form.get('rejectionReason').value === 'NOT_COMFORTABLE'
  }

  ngOnInit(): void {
    // Booking.com requires the private text with NOT_COMFORTABLE and accepts it with no other reason.
    this.form
      .get('rejectionReason')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((reason) => {
        const text = this.form.get('rejectionReasonText')
        const validators = [Validators.maxLength(BDC_RTB_REJECTION_TEXT_MAX)]
        if (reason === 'NOT_COMFORTABLE') {
          validators.push(Validators.required, Validators.pattern(/\S/))
        } else {
          text.setValue('')
        }
        text.setValidators(validators)
        text.updateValueAndValidity()
      })
  }

  @SaveForm()
  onDecline(form: FormGroup) {
    const { rejectionReason, rejectionReasonText } = this.form.getRawValue()
    this.isLoading = true
    this.rtbService
      .decide(this.data.inquiry, {
        status: 'REJECTED',
        rejectionReason,
        ...(rejectionReason === 'NOT_COMFORTABLE' ? { rejectionReasonText: (rejectionReasonText || '').trim() } : {}),
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`The booking request was declined.`)
          this.dialogRef.close(true)
        },
        (error) => {
          this.toaster.error($localize`Unable to decline the booking request`, '', error)
          this.rtbService.refresh(this.data.inquiry)
          this.dialogRef.close(false)
        }
      )
  }
}
